import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import {
  LayoutDashboard, AlertTriangle, Boxes, Truck, Wrench, Package,
  Megaphone, Handshake, MessageSquare, IdCard, FileText,
  Building2, Users, LogOut, ChevronDown, ChevronRight, Menu, X,
  Fuel, AlertOctagon, BarChart3, FileSignature, PackageOpen, Receipt,
  Star, Award, Send, ShieldCheck, History, UserSquare2,
  ShoppingBag, ShoppingCart, Newspaper, Briefcase, PackagePlus, BookLock,
  Globe2, Images, ClipboardList, GaugeCircle, Calendar as CalendarIcon,
  DatabaseBackup, LineChart, Luggage, Fingerprint,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { canViewModule } from "@/lib/moduleCatalog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import TenantLogoDialog from "@/components/TenantLogoDialog";
import { KeyRound } from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/",        moduleKey: "dashboard", label: "Executive Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/executive-rollup", moduleKey: "executive-rollup", label: "Executive Rollup", icon: LineChart, testid: "nav-executive-rollup",
        roles: ["managing_director", "executive_director", "general_manager"] },
      { to: "/reports", moduleKey: "reports",   label: "Reports & Analytics", icon: BarChart3,       testid: "nav-reports" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/issues",         moduleKey: "issues",         label: "Issue Reporting", icon: AlertTriangle, testid: "nav-issues" },
      { to: "/inventory",      moduleKey: "inventory",      label: "Stock Incidents", icon: Package,       testid: "nav-inventory" },
      { to: "/stock-requests", moduleKey: "stock-requests", label: "Stock Requests",  icon: PackagePlus,   testid: "nav-stock-requests" },
      { to: "/assets",         moduleKey: "assets",         label: "Assets",          icon: Boxes,         testid: "nav-assets" },
      { to: "/vehicles",       moduleKey: "vehicles",       label: "Vehicles",        icon: Truck,         testid: "nav-vehicles" },
      { to: "/fuel-logs",      moduleKey: "fuel-logs",      label: "Fuel Logs",       icon: Fuel,          testid: "nav-fuel" },
      { to: "/accidents",      moduleKey: "accidents",      label: "Accidents",       icon: AlertOctagon,  testid: "nav-accidents" },
      { to: "/maintenance",    moduleKey: "maintenance",    label: "Maintenance",     icon: Wrench,        testid: "nav-maintenance" },
      { to: "/luggage",        moduleKey: "luggage",        label: "Luggage Area",    icon: Luggage,       testid: "nav-luggage" },
    ],
  },
  {
    label: "Website & Sales",
    items: [
      { to: "/products",      moduleKey: "products",      label: "Products",                icon: ShoppingBag,  testid: "nav-products" },
      { to: "/orders",        moduleKey: "orders",        label: "Online Orders",           icon: ShoppingCart, testid: "nav-orders" },
      { to: "/news-admin",    moduleKey: "news-admin",    label: "News & Updates",          icon: Newspaper,    testid: "nav-news-admin" },
      { to: "/careers-admin", moduleKey: "careers-admin", label: "Careers & Applications",  icon: Briefcase,    testid: "nav-careers-admin" },
      { to: "/hero-slides",   moduleKey: "hero-slides",   label: "Homepage Banners",        icon: Images,       testid: "nav-hero-slides" },
    ],
  },
  {
    label: "Stakeholders",
    items: [
      { to: "/suppliers",       moduleKey: "suppliers",       label: "Suppliers",         icon: Handshake,     testid: "nav-suppliers" },
      { to: "/contracts",       moduleKey: "contracts",       label: "Contracts",         icon: FileSignature, testid: "nav-contracts" },
      { to: "/deliveries",      moduleKey: "deliveries",      label: "Deliveries",        icon: PackageOpen,   testid: "nav-deliveries" },
      { to: "/purchases",       moduleKey: "purchases",       label: "Purchases",         icon: Receipt,       testid: "nav-purchases" },
      { to: "/feedback",        moduleKey: "feedback",        label: "Customer Feedback", icon: MessageSquare, testid: "nav-feedback" },
      { to: "/view-feedback",   moduleKey: "feedback",        label: "View Feedback",     icon: CalendarIcon,  testid: "nav-view-feedback" },
      { to: "/branch-feedback", moduleKey: "feedback",        label: "Branch Feedback",   icon: Building2,     testid: "nav-branch-feedback" },
      { to: "/surveys",         moduleKey: "surveys",         label: "Surveys",           icon: Star,          testid: "nav-surveys" },
      { to: "/loyalty",         moduleKey: "loyalty",         label: "Loyalty Members",   icon: Award,         testid: "nav-loyalty" },
      { to: "/business-cards",  moduleKey: "business-cards",  label: "Business Cards",    icon: IdCard,        testid: "nav-business-cards" },
    ],
  },
  {
    label: "Communications",
    items: [
      { to: "/messages",      moduleKey: "messages",      label: "Internal Messages", icon: Send,     testid: "nav-messages" },
      { to: "/announcements", moduleKey: "announcements", label: "Announcements",     icon: Megaphone, testid: "nav-announcements" },
      { to: "/policy",        moduleKey: "policy",        label: "Operations Policy", icon: BookLock, testid: "nav-policy" },
      { to: "/documents",     moduleKey: "documents",     label: "Documents",         icon: FileText, testid: "nav-documents" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/branches",       moduleKey: "branches",       label: "Branches",          icon: Building2,     testid: "nav-branches" },
      { to: "/users",          moduleKey: "users",          label: "Users & Roles",     icon: Users,          testid: "nav-users" },
      { to: "/staff",          moduleKey: "staff",          label: "Staff Bio Data",    icon: UserSquare2,   testid: "nav-staff" },
      { to: "/attendance",     moduleKey: "attendance",     label: "Staff Attendance",  icon: Fingerprint,   testid: "nav-attendance" },
      { to: "/leave",          moduleKey: "leave",          label: "Leave Management",  icon: CalendarIcon,  testid: "nav-leave" },
      { to: "/admin-requests", moduleKey: "admin-requests", label: "Admin Requests",    icon: ClipboardList, testid: "nav-admin-requests" },
      { to: "/approvals",      moduleKey: "approvals",      label: "Approvals",         icon: ShieldCheck,   testid: "nav-approvals" },
      { to: "/approval-policies", moduleKey: "approvals",   label: "Approval Thresholds", icon: GaugeCircle, testid: "nav-approval-policies" },
      { to: "/periodic-reports",  moduleKey: "periodic-reports", label: "Periodic Reports", icon: FileText,    testid: "nav-periodic-reports" },
      { to: "/audit-log",      moduleKey: "audit-log",      label: "Audit Log",         icon: History,       testid: "nav-audit" },
      { to: "/backups",        moduleKey: "backups",        label: "Backups",           icon: DatabaseBackup, testid: "nav-backups", superOnly: true },
      { to: "/tenants",        moduleKey: "tenants",        label: "Tenants (SaaS)",    icon: Globe2,        testid: "nav-tenants", superOnly: true },
    ],
  },
];

function SidebarGroup({ group, onNavigate, isSuper, user }) {
  const [open, setOpen] = useState(true);
  const items = group.items.filter((it) => {
    if (it.superOnly && !isSuper) return false;
    if (it.roles && !it.roles.includes(user?.role)) return false;
    return canViewModule(user, it.moduleKey);
  });
  if (items.length === 0) return null;
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-emerald-200/60 hover:text-emerald-200 transition-colors"
        data-testid={`sidebar-group-${group.label.toLowerCase()}`}
      >
        <span>{group.label}</span>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && (
        <ul className="space-y-0.5">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  end={it.to === "/"}
                  onClick={onNavigate}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 mx-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-[#064E3B] text-white shadow-[inset_2px_0_0_0_#F59E0B]"
                        : "text-emerald-50/80 hover:bg-[#0B5C46] hover:text-white"
                    }`
                  }
                >
                  <Icon size={16} strokeWidth={2} />
                  <span className="truncate">{it.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const [tenant, setTenant] = useState(null);
  const tenantName = tenant?.name || "";
  const canEditLogo = ["managing_director", "executive_director"].includes(user?.role);
  const openPasswordDialog = () => setPasswordOpen(true);

  useEffect(() => {
    let alive = true;
    api.get("/tenants/me")
      .then(({ data }) => { if (alive && data) setTenant(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user?.tenant_slug]);

  const isSuper =
    user?.tenant_slug === "abrah" &&
    ["managing_director", "executive_director"].includes(user?.role);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebar = (
    <aside className="w-64 bg-[#022C22] text-white flex flex-col h-screen sidebar-scroll overflow-y-auto border-r border-[#064E3B]">
      <div className="px-5 py-5 border-b border-[#064E3B]">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => canEditLogo && setLogoOpen(true)}
            data-testid="tenant-logo-badge"
            title={canEditLogo ? "Change organisation logo" : undefined}
            className={`h-9 w-9 rounded-md overflow-hidden flex items-center justify-center shrink-0 ${
              tenant?.logo_file_id || !tenantName || /abrah/i.test(tenantName)
                ? "bg-white"
                : "bg-amber-500 font-display font-bold text-[#022C22]"
            } ${canEditLogo ? "cursor-pointer hover:ring-2 hover:ring-amber-400/70 transition-shadow" : "cursor-default"}`}
          >
            {tenant?.logo_file_id ? (
              <img src={fileUrl(tenant.logo_file_id)} alt={`${tenantName} logo`}
                className="h-full w-full object-contain" data-testid="tenant-logo-img" />
            ) : !tenantName || /abrah/i.test(tenantName) ? (
              <img src="/abrah-logo.jpeg" alt="Abrah Supermarket logo"
                className="h-full w-full object-contain" data-testid="tenant-logo-default" />
            ) : (
              (tenantName || "A").slice(0, 1).toUpperCase()
            )}
          </button>
          <div>
            <div className="font-display font-bold text-base leading-tight">AEOIBCMS</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-200/70 truncate max-w-[160px]"
              data-testid="sidebar-tenant-name">
              {tenantName || "Enterprise"}
            </div>
          </div>
        </div>
      </div>
      <nav className="flex-1 py-4">
        {NAV_GROUPS.map((g) => (
          <SidebarGroup key={g.label} group={g} isSuper={isSuper} user={user}
            onNavigate={() => setMobileOpen(false)} />
        ))}
      </nav>
      <div className="border-t border-[#064E3B] p-3">
        <div className="px-2 pb-2">
          <div className="text-xs font-medium text-white truncate">{user?.name}</div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-200/70">
            {user?.role?.replaceAll("_", " ")}
          </div>
        </div>
        <button
          onClick={() => { setMobileOpen(false); openPasswordDialog(); }}
          data-testid="change-password-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-emerald-50/80 hover:bg-[#0B5C46] hover:text-white transition-colors"
        >
          <KeyRound size={15} />
          Change password
        </button>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-emerald-50/80 hover:bg-[#0B5C46] hover:text-white transition-colors"
        >
          <LogOut size={15} />
          Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-[#F4F5F1]">
      {/* Desktop sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 h-screen z-40">{sidebar}</div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative z-10">{sidebar}</div>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-14">
            <div className="flex items-center gap-2">
              <button
                className="lg:hidden p-2 -ml-2 text-gray-700"
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <div className="font-display font-semibold text-sm sm:text-base text-gray-900"
                data-testid="header-tenant-name">
                {tenantName || "Enterprise Console"}{tenantName ? <> · <span className="text-emerald-800">Enterprise Console</span></> : null}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:block text-right">
                <div className="text-xs font-semibold text-gray-900">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500">
                  {user?.branch || "—"}
                </div>
              </div>
              <div className="h-9 w-9 rounded-full bg-emerald-900 text-white flex items-center justify-center font-semibold text-sm">
                {(user?.name || "U").slice(0, 1)}
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
      <TenantLogoDialog open={logoOpen} onOpenChange={setLogoOpen}
        tenant={tenant} onUpdated={setTenant} />
    </div>
  );
}
